import { EXPORT_SEARCH_URL_PARAM } from '@aibees/elephant/context/search-params-url-sync';
import { ExportItem, ExportSearchGrouping } from '@aibees/elephant/hooks';
// Import ROUTES from the routes module directly (not the `@aibees/elephant/router`
// barrel, which also re-exports the Router component). This util is reachable from
// ExportSearchLoader, which router.tsx renders — going through the barrel would
// close an import cycle.
import { ROUTES as R } from '@aibees/elephant/router/routes';
import type { SearchBrowseParams } from '@aibees/elephant/types';
import { ExportStatus } from '@aibees/shared/components/status-tag';
import { ChipProps } from '@aibees/shared/components';
import { capitalize, parseUrlQueryString } from '@aibees/shared/utils';

/**
 * Builds the "add this export as a filter" link: opens a NEW search with the
 * export applied to the include-exports filter. Short URL (export name + id), so
 * no 414 risk. Used by the "View Companies/People" action.
 */
export const getExportSearchUrl = (item: ExportItem): string => {
  const exportId = encodeURIComponent(item.id);

  if (item.grouping === ExportSearchGrouping.COMPANY) {
    return `${R.searchCompany}?companies_exports_request_include=${item.name}%3A%3A${exportId}`;
  }

  if (item.grouping === ExportSearchGrouping.PERSON) {
    return `${R.searchPeople}?people_exports_request_include=${item.name}%3A%3A${exportId}`;
  }

  return '';
};

/**
 * Legacy exports stored the original search as a `metadata.url` query string; new
 * exports store the raw params object under `metadata.params` (JSON-encoded). Read
 * `params` first (no query-string round-trip), fall back to parsing `url`.
 *
 * Shared with `useApplyExportSearch`, which hydrates the store from the same
 * resolved params on load.
 */
export function resolveExportParams(
  item?: ExportItem
): Partial<SearchBrowseParams> | null {
  if (!item) return null;

  const raw = item.metadata?.params;
  if (raw) {
    try {
      return JSON.parse(raw) as Partial<SearchBrowseParams>;
    } catch {
      // fall through to the legacy url below
    }
  }

  const url = item.metadata?.url;
  if (url) return parseUrlQueryString<Partial<SearchBrowseParams>>(url);

  return null;
}

/**
 * Builds the "View Search" link: reopens the ORIGINAL search that produced this
 * export. Carries only the short `?export-search=<id>`; `useApplyExportSearch`
 * fetches the export and hydrates the store from `metadata.params` on load, so the
 * full filter set never enters the URL (avoids a 414 for filter-heavy searches).
 */
export const getExportOriginalSearchUrl = (item: ExportItem): string => {
  const exportId = encodeURIComponent(item.id);

  if (item.grouping === ExportSearchGrouping.COMPANY) {
    return `${R.searchCompany}?${EXPORT_SEARCH_URL_PARAM}=${exportId}`;
  }

  if (item.grouping === ExportSearchGrouping.PERSON) {
    // Employees-table exports carry the source company id (`company_id`, set
    // from the route by EmployeesTable) in their stored params. Route "View
    // Search" back to that company's Employees table instead of the generic
    // People search so it reopens the same context it was exported from.
    const companyId = resolveExportParams(item)?.company_id;
    if (companyId) {
      return `${R.companyEmployees.replace(
        ':id',
        encodeURIComponent(companyId)
      )}?${EXPORT_SEARCH_URL_PARAM}=${exportId}`;
    }

    return `${R.searchPeople}?${EXPORT_SEARCH_URL_PARAM}=${exportId}`;
  }

  return '';
};

export function humanReadableExportStatus(status: ExportStatus) {
  const refactoredStatus =
    status === ExportStatus.PENDING ? ExportStatus.PROCESSING : status;
  return (
    capitalize(refactoredStatus) +
    (refactoredStatus === ExportStatus.PROCESSING ? '...' : '')
  );
}

export const colorMap: Record<ExportStatus, ChipProps['color']> = {
  [ExportStatus.FAILED]: 'error',

  [ExportStatus.PROCESSING]: 'primary',

  [ExportStatus.PENDING]: 'warning',

  [ExportStatus.UPLOADING]: 'success',

  [ExportStatus.SUCCESS]: 'primary',

  [ExportStatus.EXPORTING]: 'warning',
};
